import * as React from "react";
import * as ReactDOM from "react-dom";
import * as s from "../RealtimeIndicator/RealtimeIndicator.style.css";

export type HistoricalDataStatus =
  | "connected"
  | "simulated"
  | "simulationPaused"
  | "empty"
  | "error"
  | "notConfigured"
  | "loading";

interface HistoricalDataIndicatorProps {
  status: HistoricalDataStatus;
  source?: "timestream" | "partner-api" | null;
  domain?: string | null;
  errorMessage?: string;
}

function formatDomain(domain?: string | null): string | null {
  if (!domain) return null;
  return domain.charAt(0).toUpperCase() + domain.slice(1);
}

function sourceLabel(source?: "timestream" | "partner-api" | null): string {
  if (source === "timestream") return "Timestream";
  if (source === "partner-api") return "Partner API";
  return "Connected";
}

type StatusConfig = {
  colors: (typeof s.STATUS_COLORS)[keyof typeof s.STATUS_COLORS];
  label: (source?: "timestream" | "partner-api" | null) => string;
  title: (source?: "timestream" | "partner-api" | null) => string;
  description: (source?: "timestream" | "partner-api" | null, domain?: string | null) => string;
};

const STATUS_CONFIG: Record<HistoricalDataStatus, StatusConfig> = {
  connected: {
    colors: s.STATUS_COLORS.online,
    label: (src) => sourceLabel(src),
    title: (src) => `Historical Data: ${sourceLabel(src)}`,
    description: (src, domain) => {
      const domainStr = formatDomain(domain);
      if (src === "timestream") return `Loaded from AWS Timestream.${domainStr ? ` Domain: ${domainStr}.` : ""}`;
      if (src === "partner-api") return `Last 24 hours loaded from Partner API.${domainStr ? ` Domain: ${domainStr}.` : ""}`;
      return `Last 24 hours of historical telemetry loaded.${domainStr ? ` Domain: ${domainStr}.` : ""}`;
    },
  },
  simulated: {
    colors: s.STATUS_COLORS.simulationActive,
    label: () => "Simulating",
    title: () => "Historical Data: Simulating",
    description: () => "Historical data generated from the active simulation session.",
  },
  simulationPaused: {
    colors: s.STATUS_COLORS.simulationPaused,
    label: () => "Simulation Paused",
    title: () => "Historical Data: Simulation Paused",
    description: () => "Simulation is paused — start a session to view historical data.",
  },
  empty: {
    colors: s.STATUS_COLORS.stale,
    label: () => "No data",
    title: () => "Historical Data: No data",
    description: () => "No historical records found for the last 24 hours.",
  },
  error: {
    colors: s.STATUS_COLORS.offline,
    label: () => "Error",
    title: () => "Historical Data: Error",
    description: () => "Failed to load historical data.",
  },
  notConfigured: {
    colors: s.STATUS_COLORS.unknown,
    label: () => "Not configured",
    title: () => "Historical Data: Not configured",
    description: () => "This product does not have historical telemetry configured.",
  },
  loading: {
    colors: s.STATUS_COLORS.unknown,
    label: () => "Loading…",
    title: () => "Historical Data: Loading",
    description: () => "Fetching historical telemetry data.",
  },
};

function Dot({ color }: { color: string }) {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
      <circle cx="4" cy="4" r="4" fill={color} />
    </svg>
  );
}

function PauseSvg({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <rect x="5" y="4" width="4" height="16" rx="1" />
      <rect x="15" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

export function HistoricalDataIndicator({ status, source, domain, errorMessage }: HistoricalDataIndicatorProps) {
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{ bottom: number; left: number } | null>(null);

  const showTooltip = React.useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({ bottom: window.innerHeight - rect.top + 8, left: rect.left + rect.width / 2 });
    }
    setTooltipVisible(true);
  }, []);
  const hideTooltip = React.useCallback(() => setTooltipVisible(false), []);

  const config = STATUS_CONFIG[status];
  const colors = config.colors;
  const label = config.label(source);
  const title = config.title(source);
  const description = errorMessage && status === "error" ? errorMessage : config.description(source, domain);

  const tooltipEl = tooltipVisible && tooltipPos ? (
    <div
      className={s.tooltipPortal}
      style={{ position: "fixed", bottom: tooltipPos.bottom, left: tooltipPos.left, transform: "translateX(-50%)" }}
      role="tooltip"
    >
      <div className={s.tooltipInner} style={{ position: "relative" }}>
        <span className={s.tooltipTitle}>{title}</span>
        <span className={s.tooltipDescription}>{description}</span>
        <div className={s.tooltipCaret} />
      </div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className={s.badge}
        style={{ background: colors.background, color: colors.color, borderColor: colors.borderColor }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <span className={s.iconSlot}>
          {status === "simulationPaused" ? (
            <PauseSvg size={11} color={colors.color} />
          ) : (
            <Dot color={colors.ring} />
          )}
        </span>
        {label}
      </span>
      {typeof document !== "undefined" && ReactDOM.createPortal(tooltipEl, document.body)}
    </>
  );
}
